/**
 * What the `workspace` function refuses, checked before anything is sent — the
 * owner's rule: a raw server validation string reaching a user is a bug. Mirrors
 * `supabase/functions/workspace/validation.ts` and its service's rules.
 */

import type { Workspace, WorkspaceAllowance } from "@/services/workspace.service";

export const WORKSPACE_NAME_MAX_LENGTH = 100;

export const WORKSPACE_NAME_MESSAGES = {
  required: "Enter a workspace name.",
  tooLong: `Keep the name to ${WORKSPACE_NAME_MAX_LENGTH} characters or fewer.`,
  duplicate: "You already have a workspace with this name.",
  ownerDuplicate: "The workspace owner already has a workspace with this name.",
} as const;

/** The longest API key name the function takes, counted trimmed. */
export const API_KEY_NAME_MAX_LENGTH = 100;

/**
 * Why a key name cannot be sent, or undefined. An empty one is not checked
 * here: Create stays off until there is a name.
 */
export const apiKeyNameError = (name: string) =>
  name.trim().length > API_KEY_NAME_MAX_LENGTH
    ? `Keep the name to ${API_KEY_NAME_MAX_LENGTH} characters or fewer.`
    : undefined;

/**
 * The address rule the function runs on an invitation: zod 3.23's `.email()`,
 * on the trimmed address. It refuses some addresses a browser's `type="email"`
 * lets through: no dot after the `@` (`ann@acme`), a one-letter top-level
 * domain (`a@b.c`), or a `!` before the `@`.
 */
const INVITE_EMAIL_PATTERN = /^(?!\.)(?!.*\.\.)[A-Z0-9_'+.-]*[A-Z0-9_+-]@(?:[A-Z0-9][A-Z0-9-]*\.)+[A-Z]{2,}$/i;

/**
 * Why an invitation cannot be sent to `email`, or undefined. An empty one is
 * not checked here: Send stays off until there is an address.
 */
export function inviteEmailError(email: string): string | undefined {
  const address = email.trim();
  return !address || INVITE_EMAIL_PATTERN.test(address) ? undefined : "Enter a valid email address.";
}

/** What the workspace function's allowance is for an owner with no plan: Pro would lift it. */
export const FREE_WORKSPACE_ALLOWANCE = 1;

/**
 * Whether the plan leaves room for one more workspace, as the server decides:
 * `"pro"` when a free owner is at their one (Pro would help), `"limit"` at Pro's
 * own cap, null when there is room.
 */
export function createRefusal({ owned, allowed }: WorkspaceAllowance): "pro" | "limit" | null {
  if (owned < allowed) return null;
  return allowed <= FREE_WORKSPACE_ALLOWANCE ? "pro" : "limit";
}

/** Pro's cap, said before anything is sent. */
export const limitMessage = (allowed: number) =>
  `Workspace limit reached: the Pro plan allows up to ${allowed} workspaces.`;

/** Names are compared as the server compares them: case and surrounding spaces aside. */
const sameName = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * Why `name` cannot be saved, or undefined. `siblings` are the other workspaces
 * of the same owner that the user can see: a new name may not repeat one of
 * them. `current` is the name being edited, which may always be saved
 * unchanged. `forOwner` words a clash for the owner ("You already have…") or
 * for an admin renaming someone else's workspace.
 */
export function workspaceNameError(
  name: string,
  siblings: string[],
  current?: string,
  forOwner = true,
): string | undefined {
  const trimmed = name.trim();
  if (!trimmed) return WORKSPACE_NAME_MESSAGES.required;
  if (trimmed.length > WORKSPACE_NAME_MAX_LENGTH) return WORKSPACE_NAME_MESSAGES.tooLong;
  if (trimmed !== current && siblings.some((sibling) => sameName(sibling, trimmed))) {
    return forOwner ? WORKSPACE_NAME_MESSAGES.duplicate : WORKSPACE_NAME_MESSAGES.ownerDuplicate;
  }
  return undefined;
}

/**
 * The names a new or renamed workspace may not repeat, as the server checks
 * them: for a new one, the user's own workspaces; for a rename, the other
 * workspaces of that workspace's owner that the user can open.
 */
export function siblingNames(workspaces: Workspace[], workspace?: Workspace): string[] {
  if (!workspace) return ownedWorkspaces(workspaces).map((w) => w.name);
  return workspaces.filter((w) => w.id !== workspace.id && w.owner_id === workspace.owner_id).map((w) => w.name);
}

/** The server's matrix: owner or admin renames, only the owner deletes. */
export const canRename = (workspace: Pick<Workspace, "role">) =>
  workspace.role === "owner" || workspace.role === "admin";

/** An owner's only workspace is never deleted: nothing would be left to open. */
export const canDelete = (workspace: Pick<Workspace, "role">, ownedCount: number) =>
  workspace.role === "owner" && ownedCount > 1;

/** The workspaces the user owns — whose names a new or renamed one must not repeat. */
export const ownedWorkspaces = (workspaces: Workspace[]) =>
  workspaces.filter((workspace) => workspace.role === "owner");

/**
 * The workspace a user lands in: the oldest they own, else the oldest they
 * belong to. The list arrives newest first.
 */
export function defaultWorkspace(workspaces: Workspace[]): Workspace | undefined {
  const oldestFirst = [...workspaces].reverse();
  return oldestFirst.find((workspace) => workspace.role === "owner") ?? oldestFirst[0];
}

/** What a brand-new user's first workspace is called: "ann's Workspace" for ann@acme.com. */
export const firstWorkspaceName = (email: string | undefined) => `${email?.split("@")[0] || "My"}'s Workspace`;
